// ─────────────────────────────────────────────────────────────────────────
// FinanceDetailHeader — identity + meta chips + state-gated actions + KPI
// strip + workflow progress for the Billing / Payroll detail pages.
// Purely presentational: the host computes action gating via financeUtils.
// ─────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { Tooltip, message } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import AppButton from '../AppButton.jsx';
import { FinanceAvatar } from './FinanceRecordsTable.jsx';
import { FinanceKpiRow, FinanceStatusPill, FinanceWorkflowSteps } from './FinanceComponents.jsx';
import './finance-components.css';

function MetaChip({ chip }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(chip.copyValue ?? chip.value ?? ''));
      setCopied(true);
      message.success(`${chip.label ?? 'Value'} copied`);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      message.error('Unable to copy');
    }
  };

  return (
    <span className={`fin-meta-chip${chip.copyable ? ' fin-copy' : ''}`}>
      {chip.icon ? <span className="fin-meta-chip__icon">{chip.icon}</span> : null}
      {chip.label ? <span className="fin-meta-chip__label">{chip.label}</span> : null}
      <span className="fin-meta-chip__value">{chip.value ?? '—'}</span>
      {chip.badge ? <span className="fin-badge fin-badge--brand">{chip.badge}</span> : null}
      {chip.copyable ? (
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <button type="button" className="fin-meta-chip__copy" onClick={copy} aria-label={`Copy ${chip.label ?? 'value'}`}>
            {copied ? <CheckOutlined /> : <CopyOutlined />}
          </button>
        </Tooltip>
      ) : null}
    </span>
  );
}

export default function FinanceDetailHeader({
  name,
  designation,
  status,
  chips = [],
  actions = [],
  kpis = [],
  steps = [],
  loading = false,
}) {
  return (
    <div className="fin-detail-header">
      <div className="fin-dh__top">
        <div className="fin-dh__identity">
          <FinanceAvatar name={name} size="lg" />
          <div className="fin-dh__who">
            <div className="fin-dh__name-row">
              <h2 className="fin-dh__name">{name || 'Details'}</h2>
              {status ? <FinanceStatusPill status={status} /> : null}
            </div>
            {designation ? <span className="fin-dh__designation">{designation}</span> : null}
            {chips.length ? (
              <div className="fin-dh__chips">
                {chips.map((chip) => (
                  <MetaChip chip={chip} key={chip.key ?? chip.label} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {actions.length ? (
          <div className="fin-dh__actions">
            {actions.map((action) => {
              const button = (
                <AppButton
                  key={action.key ?? action.label}
                  variant={action.enabled ? action.variant ?? 'default' : 'default'}
                  icon={action.icon}
                  disabled={!action.enabled}
                  loading={Boolean(action.loading)}
                  onClick={action.onClick}
                >
                  {action.label}
                </AppButton>
              );
              return action.enabled || !action.disabledReason ? (
                button
              ) : (
                <Tooltip title={action.disabledReason} key={action.key ?? action.label}>
                  {/* span wrapper: antd tooltips don't fire on disabled buttons */}
                  <span className="fin-dh__action-wrap">{button}</span>
                </Tooltip>
              );
            })}
          </div>
        ) : null}
      </div>

      {steps.length ? <FinanceWorkflowSteps steps={steps} /> : null}
      {kpis.length || loading ? <FinanceKpiRow items={kpis} loading={loading} compact /> : null}
    </div>
  );
}
